/**
 * Treppan Academy - Admin Analytics Dashboard
 * Course analytics, user progress, and completion reports
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, Users, BookOpen, Award, Trophy, TrendingUp,
  Clock, Target, ChevronRight, GraduationCap, Download,
  ArrowUp, ArrowDown, Calendar
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
  AreaChart, Area
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colors = {
  background: '#0A0D14',
  surface: '#131825',
  gold: '#D4AF37',
  success: '#10B981',
  fail: '#EF4444',
  warning: '#F59E0B',
  info: '#3B82F6',
  purple: '#A855F7',
  textHeading: '#FFFFFF',
  textBody: '#94A3B8',
  textMuted: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)'
};

const CHART_COLORS = [colors.gold, colors.success, colors.info, colors.purple, colors.warning];

const StatCard = ({ icon: Icon, label, value, change, changeType, color = colors.gold }) => (
  <div 
    className="p-6 rounded-xl transition-all duration-300 hover:-translate-y-1"
    style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
  >
    <div className="flex items-center justify-between mb-4">
      <div 
        className="p-3 rounded-xl"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="h-6 w-6" style={{ color }} />
      </div>
      {change !== undefined && (
        <div className={`flex items-center gap-1 text-sm ${changeType === 'up' ? 'text-green-400' : 'text-red-400'}`}>
          {changeType === 'up' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
          {change}%
        </div>
      )}
    </div>
    <p className="text-xs uppercase tracking-widest mb-1" style={{ color: colors.textMuted }}>
      {label}
    </p>
    <p className="text-3xl font-bold" style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}>
      {value}
    </p>
  </div>
);

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/academy/admin/analytics?token=${token}&range=${timeRange}`);
      const result = await res.json();
      
      if (result.success) {
        setData(result.data);
      } else {
        toast.error('Failed to load analytics');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <BarChart3 className="h-12 w-12 mx-auto mb-4" style={{ color: colors.textMuted }} />
          <p style={{ color: colors.textHeading }}>Admin access required</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <BarChart3 className="h-12 w-12 mx-auto mb-4 animate-pulse" style={{ color: colors.gold }} />
          <p style={{ color: colors.textBody }}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const courseStats = data?.course_stats || [];
  const completionTrend = data?.completion_trend || [];
  const topLearners = data?.top_learners || [];
  const gradeDistribution = data?.grade_distribution || [];

  return (
    <div className="min-h-screen p-6 lg:p-8" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-xl" style={{ backgroundColor: `${colors.gold}15` }}>
              <BarChart3 className="h-6 w-6" style={{ color: colors.gold }} />
            </div>
            <h1 
              className="text-3xl font-bold tracking-tight"
              style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
            >
              Academy Analytics
            </h1>
          </div>
          <p style={{ color: colors.textBody }}>
            Track course performance, user progress, and certification metrics
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[140px]" style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.textHeading }}>
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
              <SelectItem value="7d" style={{ color: colors.textHeading }}>Last 7 days</SelectItem>
              <SelectItem value="30d" style={{ color: colors.textHeading }}>Last 30 days</SelectItem>
              <SelectItem value="90d" style={{ color: colors.textHeading }}>Last 90 days</SelectItem>
              <SelectItem value="all" style={{ color: colors.textHeading }}>All time</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline"
            style={{ borderColor: colors.border, color: colors.textBody }}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard 
          icon={Users} 
          label="Total Learners" 
          value={stats.total_learners || 0}
          change={12}
          changeType="up"
          color={colors.info}
        />
        <StatCard 
          icon={BookOpen} 
          label="Course Enrollments" 
          value={stats.total_enrollments || 0}
          change={8}
          changeType="up"
          color={colors.gold}
        />
        <StatCard 
          icon={Trophy} 
          label="Completions" 
          value={stats.total_completions || 0}
          change={15}
          changeType="up"
          color={colors.success}
        />
        <StatCard 
          icon={Award} 
          label="Certificates Issued" 
          value={stats.total_certificates || 0}
          change={20}
          changeType="up"
          color={colors.purple}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Completion Trend */}
        <div 
          className="rounded-xl p-6"
          style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <h3 
            className="font-semibold mb-4"
            style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
          >
            Completion Trend
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={completionTrend}>
                <defs>
                  <linearGradient id="completionGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colors.gold} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={colors.gold} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                <XAxis dataKey="date" stroke={colors.textMuted} fontSize={12} />
                <YAxis stroke={colors.textMuted} fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: colors.background, 
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: colors.textHeading }}
                />
                <Area 
                  type="monotone" 
                  dataKey="completions" 
                  stroke={colors.gold}
                  strokeWidth={2}
                  fill="url(#completionGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Grade Distribution */}
        <div 
          className="rounded-xl p-6"
          style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <h3 
            className="font-semibold mb-4"
            style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
          >
            Grade Distribution
          </h3>
          <div className="h-64 flex items-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={gradeDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {gradeDistribution.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.name === 'Distinction' ? colors.gold : entry.name === 'Pass' ? colors.success : colors.fail} 
                    />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: colors.background, 
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Course Performance */}
        <div 
          className="lg:col-span-2 rounded-xl p-6"
          style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <h3 
            className="font-semibold mb-4"
            style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
          >
            Course Performance
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={courseStats} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                <XAxis type="number" stroke={colors.textMuted} fontSize={12} />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  stroke={colors.textMuted} 
                  fontSize={12}
                  width={150}
                  tick={{ fill: colors.textBody }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: colors.background, 
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="enrollments" fill={colors.info} name="Enrollments" radius={[0, 4, 4, 0]} />
                <Bar dataKey="completions" fill={colors.success} name="Completions" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Learners */}
        <div 
          className="rounded-xl p-6"
          style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <h3 
            className="font-semibold mb-4"
            style={{ color: colors.textHeading, fontFamily: 'Outfit, sans-serif' }}
          >
            Top Learners
          </h3>
          <div className="space-y-4">
            {topLearners.length > 0 ? topLearners.map((learner, index) => (
              <div 
                key={learner.id || index}
                className="flex items-center gap-3 p-3 rounded-lg"
                style={{ backgroundColor: colors.background }}
              >
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center font-bold"
                  style={{ 
                    backgroundColor: index === 0 ? `${colors.gold}20` : index === 1 ? `${colors.textMuted}20` : `${colors.warning}20`,
                    color: index === 0 ? colors.gold : index === 1 ? colors.textMuted : colors.warning
                  }}
                >
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate" style={{ color: colors.textHeading }}>
                    {learner.name}
                  </p>
                  <p className="text-xs" style={{ color: colors.textMuted }}>
                    {learner.courses_completed} courses • {learner.certificates} certs
                  </p>
                </div>
                <Badge 
                  style={{ 
                    backgroundColor: `${colors.gold}20`,
                    color: colors.gold
                  }}
                >
                  {learner.score}%
                </Badge>
              </div>
            )) : (
              <p className="text-center py-8" style={{ color: colors.textMuted }}>
                No learners yet
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div 
        className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 rounded-xl"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <div className="text-center">
          <p className="text-sm" style={{ color: colors.textMuted }}>Avg. Completion Rate</p>
          <p className="text-2xl font-bold" style={{ color: colors.success }}>
            {stats.avg_completion_rate || 0}%
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm" style={{ color: colors.textMuted }}>Avg. Score</p>
          <p className="text-2xl font-bold" style={{ color: colors.gold }}>
            {stats.avg_score || 0}%
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm" style={{ color: colors.textMuted }}>Pass Rate</p>
          <p className="text-2xl font-bold" style={{ color: colors.info }}>
            {stats.pass_rate || 0}%
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm" style={{ color: colors.textMuted }}>Distinction Rate</p>
          <p className="text-2xl font-bold" style={{ color: colors.purple }}>
            {stats.distinction_rate || 0}%
          </p>
        </div>
      </div>
    </div>
  );
}
